import { Box, Modal, Notification, Table, Typography } from "components";
import { PageContent } from "layouts";

import { ReactComponent as AddIcon } from "assets/icon/add-icon.svg";
import { ReactComponent as EditIcon } from "assets/icon/edit.svg";
import { ReactComponent as ViewIcon } from "assets/icon/view-icon.svg";

import { compose, withAuthorize, withHooks, withTranslation } from "enhancers";

import { formatDate, gql, homePath, paths } from "utils/helper";

import { EnumEmployeeDelegationMode } from "constants/enums/employee-delegation-mode";
import { PERMISSIONS } from "constants/enums/permissions";
import { TFunction } from "i18next";
import { AppColor } from "theme/app-color";
import { ReactComponent as RedTrashIcon } from "assets/icon/red_trash.svg";
import { map } from "lodash";

interface EmployeeDelegationsIndexPageProps {
  t: TFunction;
  pageActions: any[];
  breadcrumbs: any[];
  columns: any[];
  tableData: any[];
  loading: boolean;
}

const EmployeeDelegationsIndexPage = (
  props: EmployeeDelegationsIndexPageProps
) => (
  <PageContent
    title={props.t(".title")}
    pageActions={props.pageActions}
    breadcrumbs={props.breadcrumbs}
  >
    <Box width="100%">
      <Typography variant="h4" style={{ marginBottom: 16 }}>
        {props.t(".list")}
      </Typography>

      <Table
        columns={props.columns}
        rows={props.tableData}
        loading={props.loading}
        density="compact"
        autoHeight
        disableSelectionOnClick
      />
    </Box>
  </PageContent>
);

const createBreadcrumbs = (title: string, t: (key: string) => string) => [
  { path: homePath(), label: t(".home") },
  { path: null, label: title },
];

const createPageActions = (t: (key: string) => string) => {
  const actions = [
    {
      children: t(".addEmployeeDelegation"),
      startIcon: <AddIcon />,
      color: "primary",
      permittedRoles: [PERMISSIONS.AGENT_MANAGEMENT_EDIT],
      onClick: () => {
        paths.employeeDelegationCreatePath().push();
      },
    },
  ];
  return actions;
};

const createTableColumns = (t: (key: string) => string) => [
  {
    field: "delegateName",
    headerName: t(".delegateName"),
    flex: 1,
    minWidth: 250,
  },
  {
    field: "mode",
    headerName: t(".mode"),
    width: 520,
    renderCell: ({ row }: any) => {
      const targetNames = map(
        row.targets,
        (target) => `${target.firstName} ${target.lastName}`
      );

      const getTargetDisplayText = () => {
        if (
          row.mode === EnumEmployeeDelegationMode.CHOOSE &&
          targetNames.length === 0
        ) {
          return "(ยังไม่ระบุ)";
        }

        if (targetNames.length === 0) return "";

        if (targetNames.length <= 2) {
          return `(${targetNames.join(", ")})`;
        }

        const firstThree = targetNames.slice(0, 3).join(", ");
        return `(${firstThree}...)`;
      };

      const modeText = t(`client.enum.employeeDelegationMode.${row.mode}`);
      const targetText = getTargetDisplayText();

      return `${modeText} ${targetText}`;
    },
  },
  {
    field: "updatedAt",
    headerName: t(".updatedAt"),
    minWidth: 140,
    align: "center",
    headerAlign: "center",
  },
  {
    width: 100,
    field: "actions",
    headerName: " ",
    filterable: false,
    sortable: false,
    type: "actions",
    align: "center",
    headerAlign: "center",
  },
];

const transformTableData = (
  t: (key: string) => string,
  employeeDelegationList: any[],
  hasEditPermission: boolean,
  handleDelete: (id: string) => void
) => {
  if (!employeeDelegationList) return [];

  const actions = [
    {
      Icon: hasEditPermission ? EditIcon : ViewIcon,
      onClick: (row: any) => {
        paths.employeeDelegationEditPath(row.id).push();
      },
    },
  ];

  if (hasEditPermission) {
    actions.push({
      Icon: RedTrashIcon,
      onClick: ({ row }: any) => {
        handleDelete(row);
      },
    });
  }

  return employeeDelegationList.map((employeeDelegation) => ({
    ...employeeDelegation,
    delegateName: `[${employeeDelegation.delegate.employeeCode}] ${employeeDelegation.delegate.firstName} ${employeeDelegation.delegate.lastName}`,
    updatedAt: formatDate(new Date(employeeDelegation.updatedAt), "dd/MM/yyyy"),
    actions,
  }));
};

export const EMPLOYEE_DELEGATION_API = {
  FETCH_EMPLOYEE_DELEGATIONS: gql`
    query FETCH_EMPLOYEE_DELEGATIONS {
      employeeDelegations {
        id
        delegateId
        mode
        delegate {
          id
          firstName
          lastName
          employeeCode
        }
        targets {
          id
          firstName
          lastName
        }
        updatedAt
      }
    }
  `,
  DELETE_EMPLOYEE_DELEGATION: gql`
    mutation DELETE_EMPLOYEE_DELEGATION($id: String!) {
      removeEmployeeDelegation(id: $id)
    }
  `,
};

const enhancer = compose(
  withAuthorize({}),
  withTranslation({ prefix: "pages.main.employeeDelegation.index" }),
  withHooks((props: any, hooks: any) => {
    const { useQuery, useMemo, useCallback, useMutation } = hooks;
    const { t, hasPermission } = props;

    const { data, loading } = useQuery(
      EMPLOYEE_DELEGATION_API.FETCH_EMPLOYEE_DELEGATIONS
    );
    const [deleteEmployeeDelegation] = useMutation(
      EMPLOYEE_DELEGATION_API.DELETE_EMPLOYEE_DELEGATION,
      {
        refetchQueries: [
          {
            query: EMPLOYEE_DELEGATION_API.FETCH_EMPLOYEE_DELEGATIONS,
          },
        ],
        onCompleted: () => {
          Notification.success(t(".success"));
        },
      }
    );

    const hasEditPermission = hasPermission([
      PERMISSIONS.AGENT_MANAGEMENT_EDIT,
    ]);

    const title = t(".title");

    const breadcrumbs = useMemo(() => createBreadcrumbs(title, t), [title, t]);

    const pageActions = useMemo(() => createPageActions(t), [t]);

    const columns = useMemo(() => createTableColumns(t), [t]);

    const confirmDelete = useCallback(
      async (id: string) => {
        await deleteEmployeeDelegation({
          variables: { id },
        });
      },
      [deleteEmployeeDelegation]
    );

    const handleDelete = useCallback(
      (row: any) => {
        console.log({ row });

        Modal.open({
          title: t(".confirmDeleteModal.removeTitle"),
          children: (
            <Box display="flex" flexDirection="column">
              <div style={{ marginBottom: "16px" }}>
                <Typography variant="Body/16">
                  {t(".confirmDeleteModal.info1")}
                </Typography>
                <Typography
                  variant="Subtitle/16"
                  color={AppColor["Primary/Primary"]}
                >
                  {` "${row.delegateName}" `}
                </Typography>
                <Typography variant="Body/16">
                  {t(".confirmDeleteModal.info2")}
                </Typography>
              </div>
            </Box>
          ),
          cancelButtonLabel: t(".confirmDeleteModal.cancelDelete"),
          okButtonLabel: t(".confirmDeleteModal.confirmDelete"),
          okButtonVariant: "contained",
          cancelButtonVariant: "outlined",
          onOk: async ({ close }: any) => {
            close();
            confirmDelete(row.id);
          },
        });
      },
      [confirmDelete, t]
    );

    const tableData = useMemo(
      () =>
        transformTableData(
          t,
          data?.employeeDelegations,
          hasEditPermission,
          handleDelete
        ),
      [t, hasEditPermission, data, handleDelete]
    );

    return {
      title,
      breadcrumbs,
      pageActions,
      columns,
      tableData,
      loading,
      t,
    };
  })
);

export default enhancer(EmployeeDelegationsIndexPage);
